// LEVEL / AREA / GRID / SYSTEM - one picker for every place a location is
// entered (owner, 2026-09-18): single issue, bulk issues, checklist.
//
// SEVERAL OF EACH where the caller asks for it: multiLevel, multiArea and
// (2026-11-04) multiSystem. Each keeps the singular field as the first of the
// list, which is the shape the issue stores (models/issue location).
//
// SYSTEM (owner, 2026-10-06): "add a 'system: ' just like area/level ... to
// show a drop down of systems like cable tray, power system, conduits, bus
// duct ... available during creation and during editing as a dropdown, that
// can take new entries, which will be saved and added to the full dropdown
// list of systems". The offered systems are the project's tracked
// vocabulary (back/utils/trackedBuckets, the one set of system names the
// model reader, the digest and the look-ahead all share) plus every system
// anyone has already typed on this project - so a new entry becomes part of
// the list for everybody by being used once, with no second place to
// maintain it.
//
// Every option comes from the project's own structure (GET
// /projects/:id/areas/location-options - utils/locationOptions): the
// schedule's levels, the drawn coordination zones of the current areas
// sheet plus the architectural areas, and the grid lines - the grid ONLY
// when the areas sheet was mapped to the building grid and its labels were
// read. Picking a level narrows the areas and the grid. Anything typed that
// is not in a list is kept as custom text; the server flags it so a
// clean-up can map it later.
//
//   <LocationPicker projectId value={{ level, area, grid }} onChange={(v) => ...} size="small" compact />
//   value.area is an option value ("area:<id>" | "arch:L1:E") or typed text.
import React, { useEffect, useMemo, useState } from "react";
import { Select, Space, Tooltip } from "antd";
import { url, tokenRef } from "api";
import { splitGridRefs, parseGridRef, filterKeys } from "helpers/gridRefs";
import { matchOption } from "components/SearchSelect/searchSelect";

// One fetch per project, shared by every picker on the page (a bulk sheet
// has one per row).
//
// LIGHT (?light=1): levels and areas only - no grid read (a PDF download and
// parse) and no model-level scan. On SpaceX BA03 the full read took 18-21 s
// cold, and an area-only picker (the tracking area tag) sat empty that long.
// A light caller takes the full answer when one is already here.
const cache = new Map(); // projectId[|light] -> Promise<options>
export function loadLocationOptions(projectId, light = false) {
  if (!projectId) return Promise.resolve(null);
  if (light && cache.has(projectId)) return cache.get(projectId);
  const key = light ? `${projectId}|light` : projectId;
  if (!cache.has(key)) {
    cache.set(key, fetch(`${url}/projects/${projectId}/areas/location-options${light ? "?light=1" : ""}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` },
    }).then((r) => (r.ok ? r.json() : null)).catch(() => null));
  }
  return cache.get(key);
}
export function clearLocationOptions(projectId) { cache.delete(projectId); cache.delete(`${projectId}|light`); }

export function useLocationOptions(projectId, light = false) {
  const [opts, setOpts] = useState(null);
  useEffect(() => {
    let alive = true;
    loadLocationOptions(projectId, light).then((o) => { if (alive) setOpts(o); });
    return () => { alive = false; };
  }, [projectId, light]);
  return opts;
}

// A single-choice select that also accepts a typed value: antd's "tags"
// mode, keeping only the last entry. With `typed={false}` it is a plain
// pick-one: nothing typed can come out of it.
function OneOrTyped({ value, options, placeholder, onChange, size, width, title, notFound, typed = true, disabled = false }) {
  if (!typed) {
    return (
      <Tooltip title={title}>
        <Select filterOption={matchOption}
          size={size}
          style={{ width, minWidth: width === "100%" ? 0 : width }}
          value={value || undefined}
          placeholder={placeholder}
          options={options}
          optionFilterProp="search"
          showSearch
          allowClear
          disabled={disabled}
          notFoundContent={notFound}
          onChange={(v) => onChange(v || "")}
        />
      </Tooltip>
    );
  }
  return (
    <Tooltip title={title}>
      <Select filterOption={matchOption}
        mode="tags"
        size={size}
        style={{ width, minWidth: width === "100%" ? 0 : width }}
        value={value ? [value] : []}
        placeholder={placeholder}
        options={options}
        optionFilterProp="label"
        showSearch
        allowClear
        tokenSeparators={[]}
        notFoundContent={notFound}
        onChange={(vals) => onChange((vals && vals.length) ? vals[vals.length - 1] : "")}
      />
    </Tooltip>
  );
}

// TAGGING (construction tracking, owner 2026-10-09: "area dropdown like
// issue area drop down"): `fields={["area"]}` shows the area picker alone,
// and `typed={false}` makes it pick-only, drawn zones only. A typed area on
// a control that WRITES an element's area would be a string nobody can
// group a quantity table by, and an architectural "L1 · Area E" is not a
// zone with an _id - so neither can come out of it in that mode. Same
// component, same options and grouping as the issue page, so the two can
// never offer different lists.
export default function LocationPicker({
  projectId, value = {}, onChange, size = "middle", compact = false,
  fields = ["level", "area", "grid", "system"], typed = true, disabled = false, width = null,
  // fill: every control stretches to the space it is given (a resizable
  // table column), instead of the fixed compact widths.
  fill = false,
  // multiLevel (owner, 2026-10-16: "we need multi level selection option"):
  // the level box takes several; value.levels holds them, value.level the first.
  multiLevel = false,
  // multiArea (owner, 2026-10-16: "same thing with areas, we need an option
  // for overall"): several areas, with "Overall" - the whole level - first.
  multiArea = false,
  // multiSystem (owner, 2026-11-04: "all properties, linked to, like tags,
  // areas, levels ... have option to support multiple tags, we need systems
  // to also support multiple values"): the system box takes several, typed
  // ones included; value.systems holds them, value.system the first - the
  // same pair as levels / levels above, so a caller that reads only
  // value.system keeps working.
  multiSystem = false,
  // allTags: every picked level / area shown (wrapping) rather than folded
  // into "+N" - for a narrow column where the picks are the point.
  allTags = false,
  // gridPlaceholder: the grid box's own hint ("Leave unchanged - ..." on the
  // bulk Set fields form).
  gridPlaceholder = undefined,
  // light: levels and areas only (see loadLocationOptions) - for a picker
  // that shows no grid.
  light = false,
}) {
  const opts = useLocationOptions(projectId, light);
  const level = value.level || "";

  const levelOptions = useMemo(() => ((opts && opts.levels) || []).map((l) => ({ value: l.value, label: l.label })), [opts]);

  // Systems: the tracked vocabulary first, then what people have added, each
  // group labelled so a reader can tell a project's own word from the shared
  // one. A typed value that matches neither is kept as text and becomes an
  // "added" option for the next person.
  const systemOptions = useMemo(() => {
    const list = (opts && opts.systems) || [];
    const vocab = list.filter((x) => x.source === "vocabulary").map((x) => ({ value: x.value, label: x.label }));
    const used = list.filter((x) => x.source !== "vocabulary").map((x) => ({ value: x.value, label: x.label }));
    const groups = [];
    if (vocab.length) groups.push({ label: "Tracked systems", options: vocab });
    if (used.length) groups.push({ label: "Added on this project", options: used });
    return groups.length ? groups : vocab;
  }, [opts]);

  // Areas grouped by level; a chosen level narrows them. Pick-only shows
  // the drawn zones alone, each with its colour - three BA03 zones share
  // the name "HYDRONIC WATER PIPING", and the swatch is what tells them
  // apart; the value is always the zone's own _id.
  // Several levels picked: the areas of any of them.
  const levelSet = Array.isArray(value.levels) && value.levels.length > 1 ? value.levels.join("\u0001") : level;
  const areaOptions = useMemo(() => {
    const on = levelSet ? new Set(levelSet.split("\u0001")) : null;
    const list = ((opts && opts.areas) || [])
      .filter((a) => !on || !a.level || on.has(a.level))
      // Pick-only keeps real zones (an _id to write): drawn ones, and the
      // Levels & Areas pages' zones - approved, drawn zones too. Leaving the
      // second out emptied the tracking area tag on every project that has
      // Levels & Areas (owner 2026-10-26: "not showing the area list from
      // levels, areas, systems"). Architectural "L1 · Area E" stays out.
      .filter((a) => typed || a.source === "drawn" || a.source === "levels-areas");
    const groups = new Map();
    for (const a of list) {
      const g = a.level || "Other";
      if (!groups.has(g)) groups.set(g, []);
      groups.get(g).push(typed ? { value: a.value, label: a.source === "architectural" ? a.label : `${a.label}` } : {
        value: a.value,
        search: `${a.label} ${a.level || ""}`,
        label: (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: a.color || "#3b82f6", flexShrink: 0 }} />
            {a.label}
          </span>
        ),
      });
    }
    return [...groups.entries()].map(([g, options]) => ({ label: g, options }));
  }, [opts, levelSet, typed]);

  const grid = (opts && opts.grids) || { available: false, byLevel: {} };
  const gridOptions = useMemo(() => {
    if (!grid.available) return [];
    const levels = level && grid.byLevel[level] ? [level] : Object.keys(grid.byLevel);
    const seen = new Set();
    const out = [];
    for (const l of levels) {
      const g = grid.byLevel[l];
      for (const v of [...(g.lines || []), ...(g.intersections || [])]) {
        if (seen.has(v)) continue;
        seen.add(v);
        out.push({ value: v, label: v });
      }
    }
    return out;
  }, [grid, level]);

  const set = (patch) => onChange && onChange({ ...value, ...patch });
  const w = compact
    ? { level: 80, area: 150, grid: 100, system: 130 }
    : { level: 120, area: 260, grid: 160, system: 200 };
  if (width) w.area = width;
  if (fill) Object.keys(w).forEach((k) => { w[k] = "100%"; });
  const show = new Set(fields);
  const Wrap = fill ? FillRow : Space;

  return (
    <Wrap size={4} wrap={!compact}>
      {show.has("level") && (multiLevel ? (
        <Tooltip title="The project's levels - pick one or several, or type one">
          <Select filterOption={matchOption}
            mode="tags" size={size} style={{ width: w.level === "100%" ? "100%" : Math.max(w.level, 200), minWidth: 0 }}
            value={Array.isArray(value.levels) && value.levels.length ? value.levels : (level ? [level] : [])}
            placeholder="Levels" options={levelOptions} optionFilterProp="label" showSearch allowClear maxTagCount={allTags ? undefined : "responsive"}
            aria-label="Levels"
            onChange={(vals) => { const list = (vals || []).filter(Boolean); set({ levels: list, level: list[0] || "" }); }}
          />
        </Tooltip>
      ) : <OneOrTyped size={size} width={w.level} placeholder="Level" value={level} options={levelOptions}
                  title="The project's levels - or type one"
                  onChange={(v) => set({ level: v })} />)}
      {show.has("area") && multiArea ? (
        <Tooltip title="Overall (the whole level), or one or several areas - or type one">
          <Select filterOption={matchOption}
            mode="tags" size={size} style={{ width: w.area === "100%" ? "100%" : Math.max(w.area, 240), minWidth: 0 }}
            value={Array.isArray(value.areas) && value.areas.length ? value.areas : (value.area ? [value.area] : [])}
            placeholder="Areas" optionFilterProp="label" showSearch allowClear maxTagCount={allTags ? undefined : "responsive"} aria-label="Areas"
            options={[{ label: "Whole level", options: [{ value: "Overall", label: "Overall (the whole level)" }] }, ...areaOptions]}
            onChange={(vals) => {
              const list = (vals || []).filter(Boolean);
              set({ areas: list, area: list.find((a) => a !== "Overall") || list[0] || "" });
            }}
          />
        </Tooltip>
      ) : show.has("area") && <OneOrTyped size={size} width={w.area} placeholder="Area" value={value.area || ""} options={areaOptions}
                  typed={typed} disabled={disabled}
                  title={typed ? "Drawn areas and the architectural areas, grouped by level - or type one" : "The drawn areas, grouped by level"}
                  notFound={typed ? undefined : "No approved areas drawn on this project"}
                  onChange={(v) => {
                    // Picking an area on another level moves the level with it.
                    const hit = ((opts && opts.areas) || []).find((a) => a.value === v);
                    set(hit && hit.level && hit.level !== level ? { area: v, level: hit.level } : { area: v });
                  }} />}
      {show.has("grid") && (
        <GridTags size={size} width={w.grid} value={value.grid || ""} options={gridOptions} allTags={allTags}
                  disabled={disabled} placeholder={gridPlaceholder}
                  title={grid.available
                    ? "One or several grid locations - a letter and a number in either order: I-40, I/40, 40-I, I40. Press Enter or type a comma between them."
                    : `${grid.reason || "No mapped grid yet."} Type one or several grid locations - a letter and a number in either order: I-40, 40-I, I40 - with a comma between them.`}
                  notFound={grid.available ? undefined : "No mapped grid - type it, e.g. I-40"}
                  onChange={(v) => set({ grid: v })} />
      )}
      {show.has("system") && (multiSystem ? (
        <Tooltip title="The systems tracked on this project - pick one or several, or type a new one, which is then offered to everybody on it">
          <Select filterOption={matchOption}
            mode="tags" size={size} style={{ width: w.system === "100%" ? "100%" : Math.max(w.system, 200), minWidth: 0 }}
            value={Array.isArray(value.systems) && value.systems.length ? value.systems : (value.system ? [value.system] : [])}
            placeholder="Systems" options={systemOptions} optionFilterProp="label" showSearch allowClear
            maxTagCount={allTags ? undefined : "responsive"} aria-label="Systems"
            notFoundContent="Type a system to add it"
            onChange={(vals) => { const list = (vals || []).filter(Boolean); set({ systems: list, system: list[0] || "" }); }}
          />
        </Tooltip>
      ) : <OneOrTyped size={size} width={w.system} placeholder="System" value={value.system || ""} options={systemOptions}
                  title="The systems tracked on this project - or type a new one, which is then offered to everybody on it"
                  notFound="Type a system to add it"
                  onChange={(v) => set({ system: v })} />)}
    </Wrap>
  );
}

// SEVERAL GRID LOCATIONS (owner, 2026-10-18: "make sure grids can be read
// x-y permutations, like I-40 or L-50 ... and make sure create issue and
// multiple issue and filter all allow multiple grid locations"). Each tag is
// one location; they are stored as ONE string, "I-40, L-50", which is what
// value.grid has always been, so every caller keeps working unchanged.
//
// READ AS AXES (helpers/gridRefs, the server's reader): "I40", "i/40" and
// "40-I" all become I-40 when typed, and typing any of them finds the I-40
// option. A range ("K/57-58") or a lone line ("K") is kept as typed.
export const gridTagsOf = (grid) => splitGridRefs(grid).map((r) => r.text);
export function tidyGridTag(t) {
  const r = parseGridRef(t);
  if (r && r.letters.length === 1 && r.numbers.length === 1) return `${r.letters[0]}-${r.numbers[0]}`;
  return r ? r.text : String(t || "").trim().toUpperCase();
}
export function gridFromTags(tags) {
  const out = [];
  const seen = new Set();
  for (const raw of tags || []) {
    // A pasted "I-40, L-50" or "I-40 L-50" arrives as one tag: split it.
    for (const r of splitGridRefs(raw)) {
      const t = tidyGridTag(r.text);
      const k = r.parsed ? r.keys.slice().sort().join("|") : `t:${t}`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(t);
    }
  }
  return out.join(", ");
}
export function gridOptionMatches(input, option) {
  const typed = String(input || "").trim();
  if (!typed) return true;
  const val = String((option && option.value) || "");
  const label = typeof (option && option.label) === "string" ? option.label : val;
  if (label.toUpperCase().includes(typed.toUpperCase())) return true;
  const want = filterKeys(typed).keys;
  if (!want.length) return false;
  const have = new Set((parseGridRef(val) || { keys: [] }).keys);
  return want.every((k) => have.has(k));
}
function GridTags({ value, options, onChange, size, width, title, notFound, allTags, disabled = false, placeholder = "Grid, e.g. I-40, L-50" }) {
  return (
    <Tooltip title={title}>
      <Select
        mode="tags" size={size} aria-label="Grid"
        style={{ width: width === "100%" ? "100%" : Math.max(width, 140), minWidth: 0 }}
        value={gridTagsOf(value)}
        placeholder={placeholder} options={options} showSearch allowClear disabled={disabled}
        maxTagCount={allTags ? undefined : "responsive"}
        tokenSeparators={[",", ";"]}
        filterOption={gridOptionMatches}
        notFoundContent={notFound}
        onChange={(vals) => onChange(gridFromTags(vals))}
      />
    </Tooltip>
  );
}

// The fill layout: the shown controls share the width equally.
function FillRow({ children }) {
  return (
    <div style={{ display: "flex", gap: 4, width: "100%" }}>
      {React.Children.toArray(children).filter(Boolean).map((c, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} style={{ flex: 1, minWidth: 0 }}>{c}</div>
      ))}
    </div>
  );
}
